/**
 * useNodeActions — Common action handlers for agent node toolbars
 */

import { useCallback } from 'react'
import { toast } from 'sonner'
import { useCanvasOptional } from '../../CanvasContext'
import { authenticatedFetch, getConfiguredApiBase } from '../../../../api/client'
import { useProjectId } from '../../../../contexts/ProjectIdContext'

/** Typed custom events for cross-component communication */
interface AppEventMap {
  'add-node-to-chat': { nodeId: string }
}

export function dispatchAppEvent<K extends keyof AppEventMap>(
  type: K,
  detail: AppEventMap[K],
) {
  window.dispatchEvent(new CustomEvent(type, { detail }))
}

export function listenAppEvent<K extends keyof AppEventMap>(
  type: K,
  handler: (detail: AppEventMap[K]) => void,
): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<AppEventMap[K]>).detail
    handler(detail)
  }
  window.addEventListener(type, listener)
  return () => window.removeEventListener(type, listener)
}

interface UseNodeActionsOptions {
  nodeId: string
}

export function useNodeActions({ nodeId }: UseNodeActionsOptions) {
  const canvas = useCanvasOptional()
  const projectId = useProjectId()

  const handleDelete = useCallback(() => {
    canvas?.removeNode(nodeId)
  }, [canvas, nodeId])

  const handleDuplicate = useCallback(() => {
    canvas?.duplicateNode(nodeId)
  }, [canvas, nodeId])

  const handleDownload = useCallback(async (url: string, filename: string) => {
    try {
      const response = await fetch(url)
      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    } catch (err) {
      console.error('Download failed', err)
      window.open(url, '_blank')
    }
  }, [])

  const handleAddToChat = useCallback(() => {
    dispatchAppEvent('add-node-to-chat', { nodeId })
  }, [nodeId])

  const handleSaveToLibrary = useCallback(async () => {
    try {
      const resp = await authenticatedFetch(
        `${getConfiguredApiBase()}/library/nodes`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ source_project_id: projectId, node_id: nodeId }),
        }
      )
      if (!resp.ok) {
        const err = await resp.json().catch(() => null)
        throw new Error(err?.detail || resp.statusText)
      }
      toast.success('Saved to library')
    } catch (err) {
      toast.error(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }, [nodeId, projectId])

  return { handleDelete, handleDuplicate, handleDownload, handleAddToChat, handleSaveToLibrary }
}
